import React from 'react';
import { useHistory } from 'react-router-dom';
import dayjs from 'dayjs';
import { ProcessedJob } from '../../../../../types/dentist/calendar';
import { JOB_STATUS } from '../../../../../constants/dentist/jobStatus';
import { isJobActionRequired } from '../../../../../utils/dentist/calendar/actionRequired';
import { getDentistShiftRoleLabel } from '../../../../../utils/dentist/shiftRoleLabel';
import styles from './JobSelectionModal.module.scss';

interface JobSelectionModalProps {
  isOpen: boolean;
  jobs: ProcessedJob[];
  selectedDate: Date | null;
  onClose: () => void;
  onPostNewShift: () => void;
}

const MAX_JOB_BUTTONS_PER_MODAL = 3;

const assertNeverStatus = (status: never): never => {
  throw new Error(`Unhandled dentist job status: ${status}`);
};

// T2-6191: red indicator when a completed shift has an unpaid invoice OR a
// pending DO feedback survey (shared predicate keeps every surface in sync).
const isActionRequired = (job: ProcessedJob): boolean => isJobActionRequired(job);

export const getJobSelectionNavigation = (
  job: ProcessedJob,
  visibleJobCount: number,
  unifiedEnabled: boolean,
) => {
  const params = {
    id: job.id,
    jobCount: Math.min(visibleJobCount, MAX_JOB_BUTTONS_PER_MODAL),
  };

  if (unifiedEnabled) {
    if (job.status === JOB_STATUS.BOOKED) {
      return { pathname: 'dentistJobConfirmed', state: params };
    }
    if (job.status === JOB_STATUS.COMPLETED) {
      return { pathname: 'jobCompleted', state: { jobId: job.id, jobCount: params.jobCount } };
    }
    return { pathname: 'jobPosted', state: params };
  }

  switch (job.status) {
    case JOB_STATUS.POSTED:
      if (job.hasRemovedOffers && !job.hasActiveOffers) {
        return { pathname: 'dentistCustomOffers', state: params };
      }
      return { pathname: 'jobPosted', state: params };
    case JOB_STATUS.CUSTOM:
      return { pathname: 'dentistCustomOffers', state: params };
    case JOB_STATUS.BOOKED:
      return { pathname: 'dentistJobConfirmed', state: params };
    case JOB_STATUS.COMPLETED:
      return { pathname: 'jobCompleted', state: { jobId: job.id, jobCount: params.jobCount } };
    case JOB_STATUS.EXPIRED:
      return { pathname: 'dentistCustomOffers', state: params };
    default:
      return assertNeverStatus(job.status);
  }
};

const JobSelectionModal: React.FC<JobSelectionModalProps> = ({
  isOpen,
  jobs,
  selectedDate,
  onClose,
  onPostNewShift
}) => {
  const history = useHistory();

  const getStatusIcon = (job: ProcessedJob): string => {
    // Legacy jobStatusMark array: ['', 'calendar-unfilled.svg', 'calendar-custom.svg', 'calendar-booked.svg', 'calendar-completed.svg', 'calendar-expired.svg']
    const jobStatusMark = [
      '',                         // Index 0 (not used)
      'calendar-unfilled.svg',    // Index 1: POSTED
      'calendar-custom.svg',      // Index 2: PARTIAL/CUSTOM (target icon)
      'calendar-booked.svg',      // Index 3: BOOKED/CONFIRMED
      'calendar-completed.svg',   // Index 4: COMPLETED
      'calendar-expired.svg'      // Index 5: EXPIRED
    ];
    
    // Use job.status (not effectiveStatus) for icon selection, matching legacy
    return jobStatusMark[job.status] || 'calendar-unfilled.svg';
  };

  const getStatusDisplayString = (job: ProcessedJob): string => {
    // Match legacy jobStatusName array: ['', 'Unfilled', 'Custom', 'Booked', 'Completed', 'Expired']
    const jobStatusName = [
      '',           // Index 0 (not used)
      'Unfilled',   // Index 1: POSTED
      'Custom',     // Index 2: PARTIAL/CUSTOM
      'Booked',     // Index 3: BOOKED/CONFIRMED
      'Completed',  // Index 4: COMPLETED
      'Expired'     // Index 5: EXPIRED
    ];

    let jobStatusDisplayString = jobStatusName[job.status] || '';
    
    // Special handling for PARTIAL status - check hasActiveOffers
    if (job.status === JOB_STATUS.CUSTOM) { // Status 2
      jobStatusDisplayString = job.hasActiveOffers ? 'Custom Offer waiting' : 'Unfilled';
    }
    
    // Special handling for CONFIRMED status
    if (job.status === JOB_STATUS.BOOKED) { // Status 3
      jobStatusDisplayString = 'Booked / Confirmed';
    }
    
    return jobStatusDisplayString;
  };

  const handleJobClick = (job: ProcessedJob) => {
    const unifiedEnabled = !!(window as any)?.TempStars?.FeatureFlags?.unifiedPostedShiftEnabled;
    const navigation = getJobSelectionNavigation(job, jobs.length, unifiedEnabled);
    if (navigation) {
      history.push(navigation);
    } else {
      console.warn('Unknown job status:', job.status);
    }
    onClose();
  };

  const handleOtherShifts = () => {
    // Navigate to React page listing all jobs for this date
    try {
      const dateKey = selectedDate ? selectedDate.toISOString().split('T')[0] : undefined;
      if (dateKey) {
        // T2-5769: forward jobs already in memory so the destination page renders
        // instantly instead of triggering a fresh getDentistJobs() round-trip.
        history.push({
          pathname: 'dentistJobsForDate',
          state: { dateKey, prefetchedJobs: jobs },
        });
      }
    } finally {
      onClose();
    }
  };

  const visibleJobs = jobs.slice(0, MAX_JOB_BUTTONS_PER_MODAL);
  const hasMoreJobs = jobs.length > MAX_JOB_BUTTONS_PER_MODAL;

  if (!isOpen) return null;

  return (
    <>
      <div className={styles.modalOverlay} onClick={onClose} />
      <div className={styles.modal}>
        <div className={styles.modalContent}>
          <h2 className={styles.modalTitle}>View which shift posting?</h2>
          <p className={styles.modalSubtitle}>
            Select the posting you would like to view:
          </p>

          <div className={styles.jobButtonsContainer}>
            {visibleJobs.map((job, index) => {
              const type = getDentistShiftRoleLabel(job.type);
              const date = dayjs(job.startDate).format('MMMM D');
              const time = job.shifts && job.shifts[0] 
                ? `${job.shifts[0].postedStart} - ${job.shifts[0].postedEnd}`
                : 'Time not specified';
              const status = getStatusDisplayString(job);
              // T2-5857: DO offices have multiple locations; show the
              // practice name so the office knows which location this shift
              // posting is for. Falls back to city or the legacy
              // address/locationName fields if practiceName is missing.
              const practiceName = (job as any)?.location?.practiceName;
              const cityName = (job as any)?.location?.city;
              const address = practiceName
                ? (cityName ? `${practiceName} — ${cityName}` : practiceName)
                : (job.address || job.locationName || cityName || 'Location');

              return (
                <button
                  key={job.id}
                  className={styles.jobButton}
                  onClick={() => handleJobClick(job)}
                >
                  <div className={styles.jobButtonWrapper}>
                    <div 
                      className={styles.jobIcon}
                      style={{ 
                        backgroundImage: `url('/img/${getStatusIcon(job)}')`
                      }}
                    >
                      {job.hasMessages && (
                        <span className={styles.unreadIndicator}>*</span>
                      )}
                      {isActionRequired(job) && (
                        <span className={styles.actionIndicator} title="Action Required">!</span>
                      )}
                    </div>
                    <div className={styles.jobDetails}>
                      <div className={styles.jobTitle}>
                        Shift {index + 1}: {date} - {type}
                      </div>
                      <div className={styles.jobTime}>
                        {time} - {status}
                      </div>
                      <div className={styles.jobAddress}>
                        {address}
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}

            {hasMoreJobs && (
              <button
                className={styles.actionButton}
                onClick={handleOtherShifts}
              >
                My other shifts...
              </button>
            )}

            <button
              className={styles.actionButton}
              onClick={onPostNewShift}
            >
              Find Another Temp
            </button>

            <button
              className={`${styles.actionButton} ${styles.goBackButton}`}
              onClick={onClose}
            >
              Go Back
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default JobSelectionModal;
